import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getOwner, getLastActivity } from './VentasFiltros';

const PAGE_SIZE = 20;
const COLS = ['Nombre del negocio', 'Etapa', 'Duración', 'Cuotas', 'Valor de venta', 'Lead asociado', 'Cliente asociado', 'Propietario', 'Creación', 'Última actividad', 'Acciones'];

const fmtCLP = (v) => (v == null ? '—' : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const ListaVentas = ({ proposals = [], clients = [], onOpenProposal, renderActions }) => {
  const [page, setPage] = useState(1);
  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);
  const rows = useMemo(() => [...proposals].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [proposals]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [rows.length]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const leadName = (p) => {
    if (!p.prospecto_id) return '—';
    const s = p.prospecto_snapshot;
    if (s) return s.empresa ? `${s.nombre} (${s.empresa})` : s.nombre;
    return clientMap[p.prospecto_id]?.name || '—';
  };
  const clientName = (p) => (p.client_id ? clientMap[p.client_id]?.name || '—' : '—');

  return (
    <div className="space-y-4" data-testid="lista-ventas-container">
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="lista-ventas-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>{COLS.map(h => <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={COLS.length} className="text-center text-slate-500 py-10" data-testid="lista-empty">No hay propuestas con estos filtros</td></tr>
            ) : pageRows.map(p => (
              <tr key={p.id} onClick={() => onOpenProposal?.(p)} className="border-t border-slate-100 hover:bg-orange-50 cursor-pointer transition-colors" data-testid={`lista-row-${p.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-slate-900">{p.titulo || 'Sin título'}</div>
                  <div className="font-mono text-[10px] text-slate-500">{p.folio}</div>
                </td>
                <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px] whitespace-nowrap">{p.status}</Badge></td>
                <td className="px-3 py-2.5 whitespace-nowrap">{p.meses_ejecucion ? `${p.meses_ejecucion} ${p.meses_ejecucion === 1 ? 'mes' : 'meses'}` : '—'}</td>
                <td className="px-3 py-2.5">{p.cuotas?.length || 0}</td>
                <td className="px-3 py-2.5 whitespace-nowrap font-medium">{p.precio_uf ? `UF ${p.precio_uf}` : fmtCLP(p.precio_total)}</td>
                <td className="px-3 py-2.5">{leadName(p)}</td>
                <td className="px-3 py-2.5">{clientName(p)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{getOwner(p)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(getLastActivity(p))}</td>
                <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>{renderActions?.(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span data-testid="lista-total">{rows.length} propuestas · Página {page} de {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="lista-prev-page">
            <ChevronLeft className="w-4 h-4" /> Anterior
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="lista-next-page">
            Siguiente <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListaVentas;
